import type { Precinct } from 'district-ui-client/domain/precinct'
import type { LessonInfoResponse } from 'district-ui-client/services/lesson-info'
import { gradeLessonNumber } from 'district-ui-client/tests/helpers/lesson-info'
import { range } from 'lodash'

export const reLessonInfoReading: LessonInfoResponse<typeof Precinct.RE_READING> = {
  lessons: range(1, 131).map((lessonPos, index) => ({
    id: `id-${lessonPos}`,
    lesson_position: lessonPos,
    lesson_number: `${lessonPos}`,
    map: Math.ceil(lessonPos / 20),
    name: `re_reading_lesson_name_${lessonPos}`,
    grade: Math.floor(index / 20),
    grade_position: Math.floor(index / 20),
    grade_lesson_number: gradeLessonNumber(lessonPos, 20),
  })),
}

export const reLessonInfoSpelling: LessonInfoResponse<typeof Precinct.RE_SPELLING> = {
  lessons: range(1, 97).map((lessonPos, index) => ({
    id: `id-${lessonPos}`,
    lesson_position: lessonPos,
    lesson_number: `${lessonPos}`,
    map: Math.ceil(lessonPos / 4),
    name: `re_spelling_lesson_name_${lessonPos}`,
    grade: Math.floor(index / 20),
    grade_position: Math.floor(index / 20),
    grade_lesson_number: gradeLessonNumber(lessonPos, 20),
  })),
}

export const reLessonInfoStorylands: LessonInfoResponse<typeof Precinct.RE_STORYLANDS> = {
  lessons: range(1, 26).map((lessonPos, index) => ({
    id: `id-${lessonPos}`,
    lesson_position: lessonPos,
    lesson_number: `${lessonPos}`,
    map: Math.ceil(lessonPos / 4),
    name: `re_storylands_lesson_name_${lessonPos}`,
    grade: Math.floor(index / 10),
    grade_position: Math.floor(index / 10),
    grade_lesson_number: gradeLessonNumber(lessonPos, 10),
  })),
}

/** DT is a little special. Lesson position / number appear multiple times, but with different categories.
 * ie
 * { lesson_position: 1, category: 'sight_words' }
 * { lesson_position: 1, category: 'letters_and_sounds' }
 * { lesson_position: 1, category: 'content_words' }
 * { lesson_position: 2, category: 'sight_words' }
 * { lesson_position: 2, category: 'letters_and_sounds' }
 * { lesson_position: 2, category: 'content_words' }
 * ...
 */
export const reLessonInfoDrivingTests: LessonInfoResponse<typeof Precinct.RE_DRIVING_TESTS> = {
  lessons: range(1, 46).map((lessonPos, index) => {
    const categoryLessonPos = Math.ceil(lessonPos / 3)
    return {
      id: `id-${categoryLessonPos}`,
      lesson_position: categoryLessonPos,
      lesson_number: `${categoryLessonPos}`,
      name: `re_driving_tests_lesson_name_${categoryLessonPos}`,
      grade: Math.floor(index / 9),
      grade_position: Math.floor(index / 9),
      grade_lesson_number: gradeLessonNumber(categoryLessonPos, 3),
      category: ['sight_words' as const, 'letters_and_sounds' as const, 'content_words' as const][index % 3],
    }
  }),
}

export const rexLessonInfoMyLesson: LessonInfoResponse<typeof Precinct.REX_READING> = {
  lessons: range(1, 221).map((lessonPos, index) => ({
    id: `id-${lessonPos}`,
    lesson_position: lessonPos,
    lesson_number: `${lessonPos}`,
    map: Math.ceil(lessonPos / 5),
    name: `rex_mylessons_lesson_name_${lessonPos}`,
    grade: Math.floor(index / 40),
    grade_position: Math.floor(index / 40),
    grade_lesson_number: gradeLessonNumber(lessonPos, 40),
  })),
}

export const rexLessonInfoSpelling: LessonInfoResponse<typeof Precinct.REX_SPELLING> = {
  lessons: range(1, 217).map((lessonPos, index) => ({
    id: `id-${lessonPos}`,
    lesson_position: lessonPos,
    lesson_number: `${lessonPos}`,
    map: Math.ceil(lessonPos / 9),
    name: `rex_spelling_lesson_name_${lessonPos}`,
    grade: Math.floor(index / 40),
    grade_position: Math.floor(index / 40),
    grade_lesson_number: gradeLessonNumber(lessonPos, 40),
  })),
}

export const fpLessonInfoLessons: LessonInfoResponse<typeof Precinct.FP_PEAKS> = {
  lessons: range(1, 21).map((lessonPos) => ({
    id: lessonPos,
    lesson_number: lessonPos,
    peak: lessonPos,
    name: `fp_lesson_name_${lessonPos}`,
    set: lessonPos,
    phase: Math.ceil(lessonPos / 4),
    sounds: [`fp_lesson_soundA_${lessonPos}`, `fp_lesson_soundB_${lessonPos}`],
    words: [`fp_lesson_word1_${lessonPos}`, `fp_lesson_word2_${lessonPos}`],
    books: [`fp_lesson_book1_${lessonPos}`, `fp_lesson_book2_${lessonPos}`],
  })),
}

export const msLessonInfoLessons: LessonInfoResponse<typeof Precinct.MS_LESSONS> = {
  lessons: range(1, 201).map((lessonPos, index) => ({
    id: `id-${lessonPos}`,
    lesson_position: lessonPos,
    lesson_number: `${lessonPos}`,
    map: Math.ceil(lessonPos / 40),
    name: `ms_lessons_lesson_name_${lessonPos}`,
    grade: Math.floor(index / 40),
    grade_position: Math.floor(index / 40),
    grade_lesson_number: gradeLessonNumber(lessonPos, 40),
  })),
}

/** DT is a little special. Lesson position / number appear multiple times, but with different categories.
 * ie
 * { lesson_position: 1, category: 'data' }
 * { lesson_position: 1, category: 'geometry' }
 * { lesson_position: 1, category: 'measurement' }
 * ...
 * { lesson_position: 2, category: 'data' }
 * { lesson_position: 2, category: 'geometry' }
 * { lesson_position: 2, category: 'measurement' }
 * ...
 */
export const msDrivingTestsLessons: LessonInfoResponse<typeof Precinct.MS_DRIVING_TESTS> = {
  lessons: range(1, 345).map((lessonPos, index) => {
    const categoryLessonPos = Math.ceil(lessonPos / 6)
    return {
      id: `id-${categoryLessonPos}`,
      lesson_position: categoryLessonPos,
      lesson_number: `${categoryLessonPos}`,
      name: `ms_driving_tests_lesson_name_${categoryLessonPos}`,
      grade: Math.floor(index / 18),
      grade_position: Math.floor(index / 18),
      grade_lesson_number: gradeLessonNumber(categoryLessonPos, 6),
      category: [
        'data' as const,
        'geometry' as const,
        'measurement' as const,
        'number' as const,
        'operations' as const,
        'patterns' as const,
      ][index % 6],
    }
  }),
}

export const msMentalMinuteLessons: LessonInfoResponse<typeof Precinct.MS_MENTAL_MINUTE> = {
  lessons: range(1, 146).map((lessonPos, index) => ({
    id: `id-${lessonPos}`,
    lesson_number: lessonPos,
    lesson_position: lessonPos,
    grade: Math.floor(index / 40),
    // lessons 1-4 Addition, lessons 5-8 Subtraction, ...
    category: ['Addition' as const, 'Subtraction' as const, 'Multiplication' as const, 'Division' as const][
      Math.floor(index / 4) % 4
    ],
    name: `ms_mental_minute_lesson_name_${lessonPos}`,
  })),
}

export const wlLessonInfoWriting: LessonInfoResponse<typeof Precinct.WL_WRITING> = {
  lessons: range(1, 31).map((lessonPos, index) => ({
    id: `id-${lessonPos}`,
    lesson: lessonPos,
    lesson_number: lessonPos,
    map_number: Math.ceil(index / 5),
  })),
}
